import { useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc, RouterOutputs } from "../../trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

export type Experiment = RouterOutputs["experiments"]["list"]["items"][number];

type MerchantOverrideDraft = { merchantId: string; percent: number };
type CustomerOverrideDraft = { customerIdentifier: string; included: boolean };

type Draft = {
  id: string;
  description: string;
  isActive: boolean;
  percent: number;
  merchantOverrides: MerchantOverrideDraft[];
  customerOverrides: CustomerOverrideDraft[];
};

const emptyDraft = (): Draft => ({
  id: "",
  description: "",
  isActive: false,
  percent: 0,
  merchantOverrides: [],
  customerOverrides: [],
});

const toPercent = (ratio: number) => Math.round(ratio * 100);
const toRatio = (percent: number) => percent / 100;

const draftFromExperiment = (experiment: Experiment): Draft => ({
  id: experiment.id,
  description: experiment.description ?? "",
  isActive: experiment.isActive,
  percent: toPercent(experiment.percentage),
  merchantOverrides: experiment.merchantOverrides.map((override) => ({
    merchantId: override.merchantId,
    percent: toPercent(override.percentage),
  })),
  customerOverrides: experiment.customerOverrides.map((override) => ({
    customerIdentifier: override.customerIdentifier,
    included: override.included,
  })),
});

type PercentSliderProps = {
  value: number;
  onChange: (percent: number) => void;
  id?: string;
  className?: string;
};

const PercentSlider = ({ value, onChange, id, className }: PercentSliderProps) => (
  <div className={`flex items-center gap-3 ${className ?? ""}`}>
    <Slider
      id={id}
      min={0}
      max={100}
      step={1}
      value={[value]}
      onValueChange={([percent]) => onChange(percent)}
      className="flex-1"
    />
    <span className="w-12 shrink-0 text-right text-sm tabular-nums text-gray-700">{value}%</span>
  </div>
);

type Props = {
  experiment: Experiment | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const ExperimentDialog = ({ experiment, isOpen, onClose, onSuccess }: Props) => {
  const isEdit = experiment !== null;
  const [tab, setTab] = useState("general");
  const [draft, setDraft] = useState<Draft>(() =>
    experiment ? draftFromExperiment(experiment) : emptyDraft()
  );
  const [merchantId, setMerchantId] = useState("");
  const [merchantPercent, setMerchantPercent] = useState(0);
  const [customerIdentifierInput, setCustomerIdentifierInput] = useState("");
  const [customerIncluded, setCustomerIncluded] = useState(true);

  const merchantsQuery = trpc.merchant.merchants.useQuery();
  const merchantItems = useMemo(
    () => (merchantsQuery.data ?? []).map((merchant) => ({ value: merchant.id, label: merchant.id })),
    [merchantsQuery.data]
  );

  const upsertMutation = trpc.experiments.upsert.useMutation({
    onSuccess: () => {
      toast.success(isEdit ? "Experimento actualizado" : "Experimento creado");
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error("Error guardando el experimento", { description: error.message });
    },
  });

  const canSubmit = draft.id.trim() !== "" && !upsertMutation.isLoading;

  const handleAddMerchantOverride = () => {
    if (!merchantId) return;
    if (draft.merchantOverrides.some((override) => override.merchantId === merchantId)) {
      toast.error("Ese merchant ya tiene un override");
      return;
    }
    setDraft({
      ...draft,
      merchantOverrides: [...draft.merchantOverrides, { merchantId, percent: merchantPercent }],
    });
    setMerchantId("");
    setMerchantPercent(0);
  };

  const handleAddCustomerOverride = () => {
    const identifier = customerIdentifierInput.trim();
    if (!identifier) return;
    if (draft.customerOverrides.some((override) => override.customerIdentifier === identifier)) {
      toast.error("Ese cliente ya tiene un override");
      return;
    }
    setDraft({
      ...draft,
      customerOverrides: [
        ...draft.customerOverrides,
        { customerIdentifier: identifier, included: customerIncluded },
      ],
    });
    setCustomerIdentifierInput("");
    setCustomerIncluded(true);
  };

  const handleSubmit = () => {
    upsertMutation.mutate({
      id: draft.id.trim(),
      description: draft.description,
      isActive: draft.isActive,
      percentage: toRatio(draft.percent),
      merchantOverrides: draft.merchantOverrides.map((override) => ({
        merchantId: override.merchantId,
        percentage: toRatio(override.percent),
      })),
      customerOverrides: draft.customerOverrides,
    });
  };

  const updateMerchantOverride = (id: string, percent: number) =>
    setDraft({
      ...draft,
      merchantOverrides: draft.merchantOverrides.map((override) =>
        override.merchantId === id ? { ...override, percent } : override
      ),
    });

  const updateCustomerOverride = (identifier: string, included: boolean) =>
    setDraft({
      ...draft,
      customerOverrides: draft.customerOverrides.map((override) =>
        override.customerIdentifier === identifier ? { ...override, included } : override
      ),
    });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Editar ${experiment.id}` : "Nuevo experimento"}</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="merchants">Merchants ({draft.merchantOverrides.length})</TabsTrigger>
            <TabsTrigger value="customers">Clientes ({draft.customerOverrides.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="experiment-id">
                ID {!isEdit && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="experiment-id"
                value={draft.id}
                disabled={isEdit}
                placeholder="ej. nuevo-flujo-citas"
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="experiment-description">Descripción</Label>
              <Textarea
                id="experiment-description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-6 items-end">
              <div className="space-y-2">
                <Label htmlFor="experiment-percentage">Porcentaje</Label>
                <PercentSlider
                  id="experiment-percentage"
                  value={draft.percent}
                  onChange={(percent) => setDraft({ ...draft, percent })}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="experiment-active">Activo</Label>
                <div className="flex h-10 items-center">
                  <Switch
                    id="experiment-active"
                    checked={draft.isActive}
                    onCheckedChange={(checked) => setDraft({ ...draft, isActive: checked })}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="merchants" className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-48 flex-1 space-y-2">
                <Label>Merchant</Label>
                <SearchableSelect
                  value={merchantId}
                  onValueChange={setMerchantId}
                  items={merchantItems}
                  placeholder="Seleccionar merchant"
                />
              </div>
              <div className="w-56 space-y-2">
                <Label>Porcentaje</Label>
                <PercentSlider value={merchantPercent} onChange={setMerchantPercent} className="h-10" />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={!merchantId}
                onClick={handleAddMerchantOverride}
              >
                Agregar
              </Button>
            </div>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Merchant</th>
                    <th className="w-64 px-3 py-2">Porcentaje</th>
                    <th className="w-20 px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {draft.merchantOverrides.length === 0 && (
                    <tr>
                      <td colSpan={3} className="h-16 text-center text-gray-500">
                        Sin overrides
                      </td>
                    </tr>
                  )}
                  {draft.merchantOverrides.map((override) => (
                    <tr key={override.merchantId}>
                      <td className="px-3 py-2">
                        <Badge variant="secondary">{override.merchantId}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <PercentSlider
                          value={override.percent}
                          onChange={(percent) => updateMerchantOverride(override.merchantId, percent)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              merchantOverrides: draft.merchantOverrides.filter(
                                (item) => item.merchantId !== override.merchantId
                              ),
                            })
                          }
                        >
                          Quitar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="customers" className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-40 flex-1 space-y-2">
                <Label htmlFor="customer-identifier">Identificador</Label>
                <Input
                  id="customer-identifier"
                  value={customerIdentifierInput}
                  placeholder="Teléfono, BSUID, documento..."
                  onChange={(e) => setCustomerIdentifierInput(e.target.value)}
                />
              </div>
              <label className="flex h-10 items-center gap-2 text-sm">
                <Checkbox
                  checked={customerIncluded}
                  onCheckedChange={(checked) => setCustomerIncluded(checked === true)}
                />
                Incluido
              </label>
              <Button
                type="button"
                variant="outline"
                disabled={!customerIdentifierInput.trim()}
                onClick={handleAddCustomerOverride}
              >
                Agregar
              </Button>
            </div>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Identificador</th>
                    <th className="w-24 px-3 py-2">Incluido</th>
                    <th className="w-20 px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {draft.customerOverrides.length === 0 && (
                    <tr>
                      <td colSpan={3} className="h-16 text-center text-gray-500">
                        Sin overrides
                      </td>
                    </tr>
                  )}
                  {draft.customerOverrides.map((override) => (
                    <tr key={override.customerIdentifier}>
                      <td className="px-3 py-2 font-mono text-xs">{override.customerIdentifier}</td>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={override.included}
                          onCheckedChange={(checked) =>
                            updateCustomerOverride(override.customerIdentifier, checked === true)
                          }
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              customerOverrides: draft.customerOverrides.filter(
                                (item) => item.customerIdentifier !== override.customerIdentifier
                              ),
                            })
                          }
                        >
                          Quitar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
            {upsertMutation.isLoading ? "Guardando..." : isEdit ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExperimentDialog;
